<script setup lang="ts">
/**
 * Stateless adapter for the translation highlights view: a "show premade" toggle on top of
 * `PremadeCustomTranslationEditor`. Row mapping lives in the field store; this component only
 * forwards already-projected rows and v-models the editable custom rows back out.
 */
import { ref } from "vue";
import NovaSwitch from "@/ui-kit/NovaSwitch/NovaSwitch.vue";
import PremadeCustomTranslationEditor from "@/features/experience-shared/highlights-v2/translation/components/PremadeCustomTranslationEditor.vue";
import type {
  PremadeTranslationRow,
  CustomTranslationRow,
} from "@/features/experience-shared/highlights-v2/translation/types/highlight-translation";

withDefaults(
  defineProps<{
    premadeRows?: PremadeTranslationRow[];
    sourceCountryCode?: string;
    targetCountryCode: string;
    readonly?: boolean;
  }>(),
  {
    premadeRows: () => [],
    sourceCountryCode: "en",
    readonly: false,
  }
);

const customRows = defineModel<CustomTranslationRow[]>("customRows", { required: true });

const showPremade = ref(true);
</script>

<template>
  <!-- Single root so a fallthrough data-testid from the parent lands on this section. -->
  <div>
    <div class="TranslationHighlightsSwitch">
      <NovaSwitch v-model="showPremade" />
      <span class="ml-2">{{ $t("experience.highlights.premade-content") }}</span>
    </div>
    <PremadeCustomTranslationEditor
      v-model:custom-rows="customRows"
      :premade-rows="premadeRows"
      :source-country-code="sourceCountryCode"
      :target-country-code="targetCountryCode"
      :show-premade="showPremade"
      :readonly="readonly"
    />
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/scss/utilities";

.TranslationHighlightsSwitch {
  display: flex;
  margin-top: rem(24);
  margin-bottom: rem(24);

  @include font-regular(12);
}
</style>
